import { Box, Button, TextField } from '@mui/material';
import { MprcBoxDrawer, MprcRequestErrorDialog } from 'components';
import { MprcSwitch } from "components/MprcSwitch";
import { ChannelSubscriptionsSettingsResponse, RequestChannelSubscriptionResult } from 'generated/CoreApi_v1';
import { useSnackbar } from 'notistack';
import { useCoreApi } from 'providers/CoreApiProvider';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    channelId: string;
    subtitle: string;
    onClose: (reload: boolean) => void;
}

export const RequestChannelSubcriptionDrawer = (props: IProps) => {

    const { channelId, onClose, subtitle } = props;

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const [saving, setSaving] = useState<boolean>(false);
    const [hasPass, setHasPass] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(false);
    const [settings, setSettings] = useState<ChannelSubscriptionsSettingsResponse>();

    let inputTextRef = useRef<HTMLInputElement>(null);
    let inputPassRef = useRef<HTMLInputElement>(null);

    ///
    const handleRequestSubcription = () => {
        setSaving(true);

        api.v1.knowledgeSubcription.requestChannelSubscription({ channelId, password: inputPassRef.current?.value ?? null, message: inputTextRef.current?.value ?? null })
            .then(response => {
                switch (response.data) {
                    case RequestChannelSubscriptionResult.InvalidPassword:
                        enqueueSnackbar(t("Subcriptions.RequestChannelSubscriptionDrawer.InvalidPasswordMessage"), { variant: "error" });
                        break;

                    case RequestChannelSubscriptionResult.Denied:
                        enqueueSnackbar(t("Subcriptions.RequestChannelSubscriptionDrawer.DeniedMessage"), { variant: "error" });
                        onClose(false);
                        break;

                    case RequestChannelSubscriptionResult.PendingApproval:
                        enqueueSnackbar(t("Subcriptions.RequestChannelSubscriptionDrawer.PendingApprovalMessage"), { variant: "warning" });
                        onClose(true);
                        break;

                    default:
                        enqueueSnackbar(t("Subcriptions.RequestChannelSubscriptionDrawer.RequestSuccessMessage"), { variant: "success" });
                        onClose(true);
                        break;
                }
            })
            .catch(setViewError)
            .finally(() => setSaving(false));
    };

    ///
    useEffect(() => {
        api.v1.knowledgeSubcription.channelSubscriptionsSettings({ channelId })
            .then(result => {
                setSettings(result.data);
            })
            .catch(e => {
                setViewError(e);
            });
    }, []);

    return (
        <>
            <MprcBoxDrawer
                onClose={() => onClose(false)}
                title={t("Subcriptions.RequestChannelSubscriptionDrawer.Title")}
                subtitle={subtitle}
                requestIndicator={saving ? "post" : undefined}
                content={
                    viewError
                        ? <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
                        : (
                            <>
                                {settings?.dismissApprovalWithPassword &&
                                    <MprcSwitch
                                        label={t("Subcriptions.RequestChannelSubscriptionDrawer.HasPasswordLabel")}
                                        checked={hasPass}
                                        onChange={(e) => setHasPass(e.target.checked)}
                                    />
                                }
                                {
                                    hasPass
                                        ? <TextField
                                            autoFocus
                                            fullWidth
                                            autoComplete="off"
                                            placeholder={t("Subcriptions.RequestChannelSubscriptionDrawer.PasswordPlaceholder")}
                                            inputRef={inputPassRef}
                                        />
                                        :
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            autoComplete="off"
                                            placeholder={t("Subcriptions.RequestChannelSubscriptionDrawer.RequestMessagePlaceholder")}
                                            inputRef={inputTextRef}
                                            variant="outlined"
                                            rows={10}
                                            multiline
                                            helperText={t("Subcriptions.RequestChannelSubscriptionDrawer.RequestMessageHelperText")}
                                        />
                                }

                                <Box sx={{ textAlign: "center", mt: 1 }}>
                                    <Button
                                        color="success"
                                        variant="contained"
                                        onClick={handleRequestSubcription}
                                    >
                                        {
                                            hasPass
                                                ? t("Subcriptions.RequestChannelSubscriptionDrawer.ButtonSubscribeText")
                                                : t("Subcriptions.RequestChannelSubscriptionDrawer.ButtonRequestText")
                                        }
                                    </Button>
                                </Box>
                            </>
                        )
                }
            />
        </>
    );
};
